import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../services/api';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  // Capture once — the effect below removes the token from the address bar, so
  // re-reading searchParams on a later render would come back empty.
  const [token] = useState(() => searchParams.get('token') || '');

  // The reset token is a credential and stays valid for an hour. Get it out of
  // the address bar so it can't sit in browser history or leak via Referer.
  useEffect(() => {
    if (window.location.search) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, new_password: newPassword });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <h1 style={styles.logo}>
          <span style={styles.logoTru}>Tru</span>
          <span style={styles.logoFlow}>Flow</span>
        </h1>

        <h2 style={styles.heading}>Reset your password</h2>

        {success ? (
          <>
            <p style={styles.success}>Password updated. You can now log in.</p>
            <p style={styles.backText}>
              <Link to="/login" style={styles.backLink}>Go to login</Link>
            </p>
          </>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            <div style={styles.field}>
              <label htmlFor="newPassword" style={styles.label}>New password</label>
              <input
                id="newPassword"
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                style={styles.input}
                placeholder="••••••••"
              />
            </div>

            <div style={styles.field}>
              <label htmlFor="confirmPassword" style={styles.label}>Confirm password</label>
              <input
                id="confirmPassword"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                style={styles.input}
                placeholder="••••••••"
              />
            </div>

            {error && <p style={styles.error}>{error}</p>}

            <button type="submit" disabled={loading} style={styles.button}>
              {loading ? 'Updating…' : 'Update password'}
            </button>

            <p style={styles.backText}>
              <Link to="/login" style={styles.backLink}>Back to login</Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: 'var(--paper)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px',
    boxSizing: 'border-box',
  },
  card: {
    backgroundColor: 'var(--card)',
    borderRadius: 'var(--r-card)',
    boxShadow: 'var(--sh-raise)',
    padding: '32px',
    width: '100%',
    maxWidth: '420px',
    boxSizing: 'border-box',
  },
  logo: {
    fontFamily: 'var(--font-serif)',
    fontSize: '2rem',
    fontWeight: '700',
    textAlign: 'center',
    margin: '0 0 24px 0',
    letterSpacing: '-0.5px',
  },
  logoTru: {
    color: 'var(--ink)',
  },
  logoFlow: {
    color: 'var(--signal)',
  },
  heading: {
    fontFamily: 'var(--font-serif)',
    fontSize: '1.25rem',
    fontWeight: '700',
    color: 'var(--ink)',
    margin: '0 0 24px 0',
  },
  field: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: '500',
    color: 'var(--slate)',
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '1rem',
    border: '1px solid var(--input-border)',
    borderRadius: '8px',
    outline: 'none',
    boxSizing: 'border-box',
    color: 'var(--ink)',
    backgroundColor: 'var(--card)',
  },
  error: {
    color: 'var(--stop)',
    fontSize: '0.875rem',
    margin: '0 0 16px 0',
    padding: '10px 12px',
    backgroundColor: 'var(--stop-soft)',
    border: '1px solid var(--stop-border)',
    borderRadius: '8px',
  },
  success: {
    fontSize: '0.875rem',
    color: 'var(--ink)',
    backgroundColor: 'var(--go-soft)',
    border: '1px solid var(--go-border)',
    borderRadius: '8px',
    padding: '12px',
    lineHeight: 1.5,
    marginBottom: '0',
  },
  button: {
    width: '100%',
    padding: '11px',
    backgroundColor: 'var(--signal)',
    color: 'var(--on-signal)',
    fontSize: '1rem',
    fontWeight: '600',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    marginTop: '8px',
  },
  backText: {
    textAlign: 'center',
    fontSize: '0.875rem',
    color: 'var(--mute)',
    marginTop: '24px',
    marginBottom: '0',
  },
  backLink: {
    color: 'var(--signal)',
    textDecoration: 'none',
    fontWeight: '500',
  },
};
